import React, { useEffect, useState } from "react";
import { MapPin, Trash2, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Client gift recipient book — list + delete (recipients are added at checkout gift form). */
export const RecipientBook = () => {
  const [recipients, setRecipients] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    apiClient.get("/me/recipients").then(({ data }) => setRecipients(data || [])).catch(() => setRecipients([]));
  }, []);

  const remove = async (r) => {
    setDeleting(r.recipient_id);
    try {
      await apiClient.delete(`/me/recipients/${r.recipient_id}`);
      setRecipients((list) => list.filter((x) => x.recipient_id !== r.recipient_id));
      toast.success(`« ${r.name} » supprimé de votre carnet de proches`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(null);
    }
  };

  if (recipients === null || recipients.length === 0) return null;

  return (
    <div className="mt-5 rounded-2xl p-4 bg-white border border-sand" data-testid="recipient-book-card">
      <div className="font-semibold text-sm mb-2 flex items-center gap-2">🎁 Mes proches (cadeaux)</div>
      <ul className="space-y-2">
        {recipients.map((r) => (
          <li key={r.recipient_id} className="flex items-start justify-between gap-2 rounded-xl bg-elevated px-3 py-2" data-testid={`recipient-item-${r.recipient_id}`}>
            <div className="min-w-0 text-sm">
              <div className="font-semibold">{r.name}</div>
              <div className="text-xs text-mute truncate">{r.phone} · {r.address} · {r.zone}</div>
            </div>
            <button
              onClick={() => remove(r)}
              disabled={deleting === r.recipient_id}
              data-testid={`recipient-delete-${r.recipient_id}`}
              className="shrink-0 p-1.5 rounded-lg text-mute hover:text-brand-red hover:bg-brand-red/10 disabled:opacity-50"
              title="Supprimer"
            >
              {deleting === r.recipient_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Client address book management — list + delete (addresses are added at checkout). */
export const AddressBook = () => {
  const [addresses, setAddresses] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    apiClient.get("/me/addresses").then(({ data }) => setAddresses(data || [])).catch(() => setAddresses([]));
  }, []);

  const remove = async (a) => {
    setDeleting(a.address_id);
    try {
      await apiClient.delete(`/me/addresses/${a.address_id}`);
      setAddresses((list) => list.filter((x) => x.address_id !== a.address_id));
      toast.success(`Adresse « ${a.label} » supprimée`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(null);
    }
  };

  if (addresses === null) return null;

  return (
    <div className="mt-5 rounded-2xl p-4 bg-white border border-sand" data-testid="address-book-card">
      <div className="font-semibold text-sm mb-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-red" /> Mes adresses</div>
      {addresses.length === 0 ? (
        <p className="text-xs text-mute">Aucune adresse enregistrée — sauvegardez-en une lors de votre prochaine commande.</p>
      ) : (
        <ul className="space-y-2">
          {addresses.map((a) => (
            <li key={a.address_id} className="flex items-start justify-between gap-2 rounded-xl bg-elevated px-3 py-2" data-testid={`address-item-${a.address_id}`}>
              <div className="min-w-0 text-sm">
                <div className="font-semibold flex items-center gap-1.5">
                  {a.label}
                  {a.gps?.lat != null && <span className="text-[9px] font-bold rounded-full px-1.5 py-0.5 bg-brand-green/15 text-brand-green">GPS</span>}
                </div>
                <div className="text-xs text-mute truncate">{a.address} · {a.zone}</div>
              </div>
              <button
                onClick={() => remove(a)}
                disabled={deleting === a.address_id}
                data-testid={`address-delete-${a.address_id}`}
                className="shrink-0 p-1.5 rounded-lg text-mute hover:text-brand-red hover:bg-brand-red/10 disabled:opacity-50"
                title="Supprimer"
              >
                {deleting === a.address_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
